"use client"

import * as React from "react"
import { MenuIcon, SettingsIcon, UserIcon, XIcon } from "lucide-react"
import { cn } from "@workspace/ui/lib/utils"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@workspace/ui/components/sheet"
import { UITypography } from "../display/ui-typography"
import { UIButton } from "../buttons/ui-button"
import { UIBox } from "./ui-box"

// ============================================================================
// Base Sheet Components (re-export with UI* naming)
// ============================================================================

export const UISheet = Sheet
export const UISheetTrigger = SheetTrigger
export const UISheetClose = SheetClose
export const UISheetHeader = SheetHeader
export const UISheetFooter = SheetFooter
export const UISheetTitle = SheetTitle
export const UISheetDescription = SheetDescription

// Enhanced SheetContent — delegates width handling to the base SheetContent
// which already applies responsive w-3/4 sm:max-w-sm (or side-specific widths)
export const UISheetContent = React.forwardRef<
  React.ComponentRef<typeof SheetContent>,
  React.ComponentPropsWithoutRef<typeof SheetContent>
>(({ className, children, ...props }, ref) => {
  return (
    <SheetContent
      ref={ref}
      className={cn(className)}
      {...props}
    >
      {children}
    </SheetContent>
  )
})
UISheetContent.displayName = "UISheetContent"

// ============================================================================
// Mobile-Optimized Preset Components
// ============================================================================

// Mobile Sheet - Bottom sheet for mobile, side sheet for desktop
export interface UISheetMobileProps {
  trigger?: React.ReactNode
  title?: string
  description?: string
  children: React.ReactNode
  footer?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

export const UISheetMobile = React.forwardRef<
  React.ComponentRef<typeof Sheet>,
  UISheetMobileProps
>(({ trigger, title, description, children, footer, open, onOpenChange, className, ...props }, ref) => {
  return (
    <UISheet open={open} onOpenChange={onOpenChange} {...props}>
      {trigger && <UISheetTrigger asChild>{trigger}</UISheetTrigger>}
      <UISheetContent
        side="bottom"
        className={cn(
          // Mobile-first optimization
          "sm:side-right", // Desktop: right side
          "max-h-[85vh] sm:max-h-full", // Mobile: limit height
          "rounded-t-lg sm:rounded-none", // Mobile: rounded top corners
          "border-t sm:border-l sm:border-t-0", // Mobile: top border, Desktop: left border
          className
        )}
      >
        {(title || description) && (
          <UISheetHeader>
            {title && <UISheetTitle>{title}</UISheetTitle>}
            {description && <UISheetDescription>{description}</UISheetDescription>}
          </UISheetHeader>
        )}

        <UIBox className="flex-1 overflow-y-auto px-4">
          {children}
        </UIBox>

        {footer && (
          <UISheetFooter className="flex-row gap-2 sm:flex-col">
            {footer}
          </UISheetFooter>
        )}
      </UISheetContent>
    </UISheet>
  )
})
UISheetMobile.displayName = "UISheetMobile"

// Navigation Sheet - Mobile navigation menu
export interface UISheetNavigationProps {
  trigger?: React.ReactNode
  title?: string
  navigationItems: Array<{
    icon?: React.ComponentType<{ className?: string }>
    label: string
    href?: string
    onClick?: () => void
    active?: boolean
    badge?: string | number
  }>
  footer?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

export const UISheetNavigation = React.forwardRef<
  React.ComponentRef<typeof Sheet>,
  UISheetNavigationProps
>(({ trigger, title = "Navigation", navigationItems, footer, open, onOpenChange, className, ...props }, ref) => {
  return (
    <UISheet open={open} onOpenChange={onOpenChange} {...props}>
      {trigger ? (
        <UISheetTrigger asChild>{trigger}</UISheetTrigger>
      ) : (
        <UISheetTrigger asChild>
          <UIButton variant="ghost" size="icon">
            <MenuIcon className="h-4 w-4" />
            <UITypography as="span" variant="small" className="sr-only">Open navigation menu</UITypography>
          </UIButton>
        </UISheetTrigger>
      )}

      <UISheetContent
        side="left"
        className={cn(
          "w-72 sm:w-80", // Fixed width for navigation
          className
        )}
      >
        <UISheetHeader>
          <UISheetTitle>{title}</UISheetTitle>
        </UISheetHeader>

        <nav className="flex-1 overflow-y-auto">
          <UIBox className="space-y-1 p-4">
            {navigationItems.map((item, index) => {
              const ItemIcon = item.icon
              const isActive = item.active

              const content = (
                <UIBox className={cn(
                  "flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  isActive && "bg-accent text-accent-foreground font-medium"
                )}>
                  <UIBox className="flex items-center gap-3">
                    {ItemIcon && <ItemIcon className="h-4 w-4" />}
                    <UITypography as="span" variant="small">{item.label}</UITypography>
                  </UIBox>
                  {item.badge && (
                    <UITypography
                      as="span"
                      variant="caption"
                      size="xs"
                      className="ml-auto rounded-full bg-primary px-2 py-0.5 text-primary-foreground"
                    >
                      {item.badge}
                    </UITypography>
                  )}
                </UIBox>
              )

              if (item.href) {
                return (
                  <a key={index} href={item.href} className="block">
                    {content}
                  </a>
                )
              }

              return (
                <button
                  key={index}
                  onClick={item.onClick}
                  className="w-full text-left"
                  type="button"
                >
                  {content}
                </button>
              )
            })}
          </UIBox>
        </nav>

        {footer && <UISheetFooter>{footer}</UISheetFooter>}
      </UISheetContent>
    </UISheet>
  )
})
UISheetNavigation.displayName = "UISheetNavigation"

// Settings Sheet - Mobile settings panel
export interface UISheetSettingsProps {
  trigger?: React.ReactNode
  title?: string
  children: React.ReactNode
  actions?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

export const UISheetSettings = React.forwardRef<
  React.ComponentRef<typeof Sheet>,
  UISheetSettingsProps
>(({ trigger, title = "Settings", children, actions, open, onOpenChange, className, ...props }, ref) => {
  return (
    <UISheet open={open} onOpenChange={onOpenChange} {...props}>
      {trigger ? (
        <UISheetTrigger asChild>{trigger}</UISheetTrigger>
      ) : (
        <UISheetTrigger asChild>
          <UIButton variant="ghost" size="icon">
            <SettingsIcon className="h-4 w-4" />
            <UITypography as="span" variant="small" className="sr-only">Open settings</UITypography>
          </UIButton>
        </UISheetTrigger>
      )}

      <UISheetContent
        side="right"
        className={cn(
          "w-full sm:w-96", // Full width on mobile, fixed width on desktop
          className
        )}
      >
        <UISheetHeader>
          <UISheetTitle>{title}</UISheetTitle>
        </UISheetHeader>

        <UIBox className="flex-1 overflow-y-auto space-y-6 px-4 py-4">
          {children}
        </UIBox>

        {actions && (
          <UISheetFooter className="flex-row gap-2">
            {actions}
          </UISheetFooter>
        )}
      </UISheetContent>
    </UISheet>
  )
})
UISheetSettings.displayName = "UISheetSettings"

// User Profile Sheet - Mobile user profile/menu
export interface UISheetUserProps {
  trigger?: React.ReactNode
  user?: {
    name: string
    email: string
    avatar?: string
  }
  menuItems?: Array<{
    icon?: React.ComponentType<{ className?: string }>
    label: string
    href?: string
    onClick?: () => void
    variant?: "default" | "destructive"
  }>
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

export const UISheetUser = React.forwardRef<
  React.ComponentRef<typeof Sheet>,
  UISheetUserProps
>(({ trigger, user, menuItems = [], open, onOpenChange, className, ...props }, ref) => {
  return (
    <UISheet open={open} onOpenChange={onOpenChange} {...props}>
      {trigger ? (
        <UISheetTrigger asChild>{trigger}</UISheetTrigger>
      ) : (
        <UISheetTrigger asChild>
          <UIButton variant="ghost" size="icon">
            <UserIcon className="h-4 w-4" />
            <UITypography as="span" variant="small" className="sr-only">Open user menu</UITypography>
          </UIButton>
        </UISheetTrigger>
      )}

      <UISheetContent
        side="right"
        className={cn(
          "w-72 sm:w-80",
          className
        )}
      >
        <UISheetHeader>
          <UISheetTitle>Account</UISheetTitle>
        </UISheetHeader>

        <UIBox className="flex-1 space-y-6 px-4">
          {/* User Info */}
          {user && (
            <UIBox className="flex items-center gap-3 p-4 rounded-lg bg-muted">
              <UIBox className="h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full" />
                ) : (
                  <UITypography
                    as="span"
                    variant="body1"
                    weight="medium"
                    className="text-accent-foreground"
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </UITypography>
                )}
              </UIBox>
              <UIBox className="flex-1 min-w-0">
                <UITypography variant="p" weight="medium" className="truncate">{user.name}</UITypography>
                <UITypography variant="p" size="sm" color="muted" className="truncate">{user.email}</UITypography>
              </UIBox>
            </UIBox>
          )}

          {/* Menu Items */}
          {menuItems.length > 0 && (
            <nav className="space-y-1">
              {menuItems.map((item, index) => {
                const ItemIcon = item.icon
                const isDestructive = item.variant === "destructive"

                const content = (
                  <UIBox className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    isDestructive && "text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  )}>
                    {ItemIcon && <ItemIcon className="h-4 w-4" />}
                    <UITypography as="span" variant="small">{item.label}</UITypography>
                  </UIBox>
                )

                if (item.href) {
                  return (
                    <a key={index} href={item.href} className="block">
                      {content}
                    </a>
                  )
                }

                return (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className="w-full text-left"
                    type="button"
                  >
                    {content}
                  </button>
                )
              })}
            </nav>
          )}
        </UIBox>
      </UISheetContent>
    </UISheet>
  )
})
UISheetUser.displayName = "UISheetUser"

// ============================================================================
// Type Exports
// ============================================================================

export type UISheetProps = React.ComponentProps<typeof Sheet>
export type UISheetTriggerProps = React.ComponentProps<typeof SheetTrigger>
export type UISheetContentProps = React.ComponentPropsWithoutRef<typeof UISheetContent>
export type UISheetHeaderProps = React.ComponentProps<typeof SheetHeader>
export type UISheetFooterProps = React.ComponentProps<typeof SheetFooter>
export type UISheetTitleProps = React.ComponentProps<typeof SheetTitle>
export type UISheetDescriptionProps = React.ComponentProps<typeof SheetDescription>